// ============================================
// TASK-9855 slice 1 — open uploaded files through the Storage SDK, by PATH.
// ============================================
// Side-effect module (UMD): attaches `window.StorageFile`. app.js imports it
// ONCE and calls `StorageFile.init({...})` with the SDK functions; every view
// reads it through `window.StorageFile` at CALL time — never a cross-`?v=` ES
// import (Pitfall #1). Node tests require() it for the pure half:
// `node tests/task9855-storage-path.unit.cjs`.
//
// WHY. Every upload writer saved a `getDownloadURL()` token URL on the doc and
// the views rendered it straight into <img src> / <a href>. A token URL is a
// bearer link that never expires and bypasses storage.rules completely, so
// whoever can read the doc (often every driver session) holds a permanent
// key to the file. This module makes the in-app READ go through the SDK on the
// object's PATH (getBlob → object URL), so storage.rules decides — the token is
// no longer what lets the file open. (Revoking the tokens is a later slice.)
//
// NOT getDownloadURL: it hands back the same permanent token URL, so it would
// keep the bearer link in the DOM. getBlob fetches the bytes with the user's
// auth and the rules are checked on every read.
//
// PATH FIRST, URL SECOND. A doc may carry a path, a URL, or both
// (memory: "doc links fail BOTH ways"). fileRef() reads the stored path when
// there is one and otherwise derives it from the URL — every Firebase token
// URL carries its object path (/o/<encoded path>).
//
// HOW A VIEW USES IT
//   <img ${StorageFile.imgAttrs(photo, { urlKey: 'thumbUrl' })} alt="">
//   <a ${StorageFile.linkAttrs(doc)}>…</a>
//   StorageFile.open(doc)                      // from a click handler
// imgAttrs / linkAttrs emit data-sf-* attributes and NO bearer URL; a
// MutationObserver hydrates <img data-sf-path> with an object URL when it
// scrolls into view, and one delegated click handler opens [data-sf-open].
//
// FAILURE POLICY (transitional, until the tokens are revoked):
//   • denied (storage/unauthorized|unauthenticated) → a registered DOOR, if the
//     element names one (driver vehicle docs → getDriverVehicleDocBytes);
//     otherwise "no access". NEVER the stored URL — a denial must not open a
//     bearer link (same stance as compliance.js openDocRowBytes).
//   • not found → "file missing". Never the URL (it would 404 too).
//   • network / CORS / unknown → the stored URL, if there is one, so an offline
//     driver or a CORS-misconfigured host still sees the file while tokens live.
//     Never on a DOOR ref (TASK-10086-N): the door's class check decides, and a
//     vehicle doc's stored link would open a paper it withholds from drivers.
//     Counted in StorageFile.stats.urlFallback and warned to the console.
//   • a value that is not a Storage URL (external link, data:image) → used as is.
(function (root, factory) {
  const api = factory(root);
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (root && typeof root === 'object') root.StorageFile = api;
})(typeof window !== 'undefined' ? window : null, function (root) {
  'use strict';

  // ---------------------------------------------------------------------------
  // PURE HALF — no DOM, no SDK. Node-tested.
  // ---------------------------------------------------------------------------

  const MAX_PATH_LEN = 1024;

  function isSafePath(p) {
    if (typeof p !== 'string') return false;
    if (!p || p.length > MAX_PATH_LEN) return false;
    if (p.charAt(0) === '/') return false;
    if (/[\u0000-\u001f\u007f]/.test(p)) return false;
    const segs = p.split('/');
    for (let i = 0; i < segs.length; i++) {
      const s = segs[i];
      if (s === '' || s === '.' || s === '..') return false;
    }
    return true;
  }

  function safeDecode(s) {
    try { return decodeURIComponent(s); } catch (_) { return null; }
  }

  // Bucket names: lowercase letters, digits, dash, underscore, dot. 3..222.
  function isPlausibleBucket(b) {
    return typeof b === 'string' && /^[a-z0-9][a-z0-9._-]{1,220}[a-z0-9]$/.test(b);
  }

  // Parse a Cloud Storage object URL into { bucket, path }. Returns null for
  // anything that is not one (external links, data:/blob: URLs, garbage).
  // Forms handled:
  //   https://firebasestorage.googleapis.com/v0/b/{bucket}/o/{enc path}?alt=media&token=…
  //   http://127.0.0.1:9199/v0/b/{bucket}/o/{enc path}?…   (emulator)
  //   https://storage.googleapis.com/{bucket}/{path}?X-Goog-…  (signed / public)
  //   https://{bucket}.storage.googleapis.com/{path}
  //   https://storage.cloud.google.com/{bucket}/{path}
  //   gs://{bucket}/{path}
  function pathFromUrl(url) {
    if (typeof url !== 'string') return null;
    const raw = url.trim();
    if (!raw) return null;

    let m = /^gs:\/\/([^/]+)\/(.+)$/i.exec(raw);
    if (m) {
      const path = m[2];
      return (isPlausibleBucket(m[1]) && isSafePath(path)) ? { bucket: m[1], path } : null;
    }

    let u;
    try { u = new URL(raw); } catch (_) { return null; }
    if (u.protocol !== 'https:' && u.protocol !== 'http:') return null;
    const host = u.hostname.toLowerCase();
    // URL.pathname keeps percent-escapes; an encoded '/' (%2F) stays encoded.
    const pn = u.pathname;

    // Firebase REST form (prod host, or any host on the emulator shape).
    m = /^\/v0\/b\/([^/]+)\/o\/(.+)$/.exec(pn);
    if (m && (host === 'firebasestorage.googleapis.com' || host === 'localhost' || host === '127.0.0.1')) {
      if (host !== 'firebasestorage.googleapis.com' && u.protocol !== 'http:') return null;
      const bucket = safeDecode(m[1]);
      const path = safeDecode(m[2]);
      if (!isPlausibleBucket(bucket) || !isSafePath(path)) return null;
      return { bucket, path };
    }

    if (host === 'storage.googleapis.com' || host === 'storage.cloud.google.com') {
      m = /^\/([^/]+)\/(.+)$/.exec(pn);
      if (!m) return null;
      const bucket = safeDecode(m[1]);
      const path = m[2].split('/').map(safeDecode);
      if (path.some(s => s === null)) return null;
      const p = path.join('/');
      return (isPlausibleBucket(bucket) && isSafePath(p)) ? { bucket, path: p } : null;
    }

    m = /^(.+)\.storage\.googleapis\.com$/.exec(host);
    if (m) {
      const segs = pn.replace(/^\//, '').split('/').map(safeDecode);
      if (segs.some(s => s === null)) return null;
      const p = segs.join('/');
      return (isPlausibleBucket(m[1]) && isSafePath(p)) ? { bucket: m[1], path: p } : null;
    }
    return null;
  }

  function isStorageUrl(url) { return !!pathFromUrl(url); }

  // The path field(s) that sit beside a URL field. Writers in this codebase
  // used several names; read every one that is in use before deriving.
  //   url      → path | storagePath | fullPath     (vehicle docs, receipts[])
  //   fullUrl  → fullPath                         (job permits/photos, ticket docs)
  //   thumbUrl → thumbPath
  //   fooUrl   → fooPath | fooStoragePath          (receiptUrl → receiptPath …)
  function pathKeysFor(urlKey) {
    const k = String(urlKey || 'url');
    if (k === 'url') return ['path', 'storagePath', 'fullPath'];
    if (k === 'fullUrl') return ['fullPath', 'storagePath', 'path'];
    if (k === 'thumbUrl') return ['thumbPath'];
    if (/Url$/.test(k)) {
      const base = k.slice(0, -3);
      return [base + 'Path', base + 'StoragePath'];
    }
    return [k + 'Path'];
  }

  const URL_KEY_ORDER = ['fullUrl', 'url', 'downloadUrl', 'thumbUrl'];

  // Normalise anything a view holds into { path, bucket, url, source }:
  //   • a string URL       → derived path (or, for a non-Storage URL, url only)
  //   • a string path      → path only
  //   • an object          → its stored path for `urlKey` if set, else derived
  // `source` = 'path' (stored) | 'url' (derived) | 'external' | null.
  // Returns null when there is nothing to open.
  function fileRef(input, opts) {
    const o = opts || {};
    if (input == null) return null;
    if (typeof input === 'string') {
      const s = input.trim();
      if (!s) return null;
      const parsed = pathFromUrl(s);
      if (parsed) return { path: parsed.path, bucket: parsed.bucket, url: s, source: 'url' };
      if (/^(https?:|data:|blob:)/i.test(s)) return { path: null, bucket: null, url: s, source: 'external' };
      if (isSafePath(s)) return { path: s, bucket: o.bucket || null, url: null, source: 'path' };
      return null;
    }
    if (typeof input !== 'object') return null;

    let urlKey = o.urlKey;
    if (!urlKey) {
      for (let i = 0; i < URL_KEY_ORDER.length; i++) {
        if (typeof input[URL_KEY_ORDER[i]] === 'string' && input[URL_KEY_ORDER[i]]) { urlKey = URL_KEY_ORDER[i]; break; }
      }
      urlKey = urlKey || 'url';
    }
    const url = (typeof input[urlKey] === 'string' && input[urlKey].trim()) ? input[urlKey].trim() : null;
    const keys = o.pathKey ? [o.pathKey] : pathKeysFor(urlKey);
    let stored = null;
    for (let i = 0; i < keys.length; i++) {
      const v = input[keys[i]];
      if (typeof v === 'string' && isSafePath(v.trim())) { stored = v.trim(); break; }
    }
    const parsed = url ? pathFromUrl(url) : null;
    if (stored) {
      // Same object as the URL? keep its bucket. Otherwise default bucket.
      const own = (typeof input.bucket === 'string' && isPlausibleBucket(input.bucket)) ? input.bucket : null;
      const bucket = (parsed && parsed.path === stored) ? parsed.bucket : (own || o.bucket || null);
      return { path: stored, bucket, url, source: 'path' };
    }
    if (parsed) return { path: parsed.path, bucket: parsed.bucket, url, source: 'url' };
    if (url && /^(https?:|data:|blob:)/i.test(url)) return { path: null, bucket: null, url, source: 'external' };
    return null;
  }

  // ---------------------------------------------------------------------------
  // BROWSER HALF — needs init() with the SDK.
  // ---------------------------------------------------------------------------

  const PLACEHOLDER = 'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==';
  // Types a blob: URL in the app origin may carry. Anything else is re-typed
  // octet-stream so it downloads instead of rendering (stored-XSS fence — same
  // concern as storage.rules isImage() / TASK-1037).
  const SAFE_TYPE = /^(image\/(jpeg|jpg|pjpeg|png|gif|webp|heic|heif|bmp|tiff|avif)|application\/pdf)$/i;
  const DENIED = ['storage/unauthorized', 'storage/unauthenticated'];
  const MISSING = ['storage/object-not-found', 'storage/bucket-not-found', 'storage/invalid-url', 'storage/invalid-argument', 'storage/no-default-bucket'];
  const CACHE_MAX = 300;
  const MAX_CONCURRENT = 6;

  let _sdk = null;
  const _doors = Object.create(null);
  // Fix 2026-10-19: a door may also hand out a short-lived signed LINK to the
  // object (iOS app shell only — it cannot open a blob:). Same server checks.
  const _doorLinks = Object.create(null);
  const _cache = new Map(); // key → { promise, objectUrl }
  const stats = { sdk: 0, door: 0, urlFallback: 0, denied: 0, missing: 0 };
  // COD (Code Reviewer) — the cache is per SIGNED-IN IDENTITY. A read that rules
  // allowed for one user must never be served to the next user of the same tab
  // (shared yard tablet: sign out, sign in as someone else). The key carries the
  // uid, and reset() — called by app.js on every auth change — revokes every
  // object URL and bumps a generation so a read still in flight for the old user
  // can never land in the cache or reach the page.
  let _gen = 0;
  function _identity() {
    try { return (_sdk && typeof _sdk.identity === 'function' && _sdk.identity()) || ''; } catch (_) { return ''; }
  }
  function reset() {
    _gen++;
    // COD fold: a viewer left open must not show the previous user's file to
    // the next user of a shared tablet.
    try { const v = root && root.document && root.document.getElementById && root.document.getElementById('sf-viewer'); if (v) { if (typeof v.__sfClose === 'function') v.__sfClose(); else v.remove(); } } catch (_) {}
    _cache.forEach((e) => { if (e && e.objectUrl) { try { URL.revokeObjectURL(e.objectUrl); } catch (_) {} } });
    _cache.clear();
  }

  function init(sdk) {
    if (!sdk || typeof sdk.getBlob !== 'function' || typeof sdk.ref !== 'function' || !sdk.storage) {
      throw new Error('StorageFile.init needs { storage, ref, getBlob }');
    }
    _sdk = sdk;
    _installDom();
  }

  // registerDoor(name, bytesFn, { link }) — link({ path, bucket, args }) resolves
  // to an https signed URL for exactly that object, or throws the door's refusal.
  function registerDoor(name, fn, opts) {
    if (typeof name === 'string' && typeof fn === 'function') _doors[name] = fn;
    if (typeof name === 'string' && opts && typeof opts.link === 'function') _doorLinks[name] = opts.link;
  }

  // A door's link is accepted only when it is a SHORT-LIVED signed URL for the
  // object the element names: https on storage.googleapis.com (path style, what
  // the server signs), a V4 signature with an expiry, the same path, and the
  // ref's bucket, or the app's own default bucket for a path-only ref (COD fold:
  // never "any bucket"). Never a token URL, never another object, never
  // something that would navigate the app itself away.
  const LINK_MAX_SECONDS = 15 * 60;
  function _defaultBucket() {
    try { return (_sdk && _sdk.storage && _sdk.storage.app && _sdk.storage.app.options && _sdk.storage.app.options.storageBucket) || null; } catch (_) { return null; }
  }
  function _isSignedLinkFor(url, r) {
    if (typeof url !== 'string' || !/^https:\/\//i.test(url)) return false;
    let u;
    try { u = new URL(url); } catch (_) { return false; }
    if (u.hostname.toLowerCase() !== 'storage.googleapis.com') return false;
    const exp = Number(u.searchParams.get('X-Goog-Expires'));
    if (!u.searchParams.get('X-Goog-Signature') || !(exp > 0 && exp <= LINK_MAX_SECONDS)) return false;
    const p = pathFromUrl(url);
    const bucket = (r && r.bucket) || _defaultBucket();
    return !!(p && r && bucket && p.path === r.path && p.bucket === bucket);
  }

  function _esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function _safeExternal(url) {
    const s = String(url || '');
    if (/^https?:\/\//i.test(s) || /^blob:/i.test(s) || /^data:image\/(png|jpe?g|gif|webp);/i.test(s)) return s;
    return '';
  }

  function _doorAttrs(o) {
    if (!o || !o.door) return '';
    let out = ` data-sf-door="${_esc(o.door)}"`;
    if (o.doorArgs && typeof o.doorArgs === 'object') {
      try { out += ` data-sf-door-args="${_esc(JSON.stringify(o.doorArgs))}"`; } catch (_) {}
    }
    return out;
  }

  // The stored URL rides along ONLY as the transitional network-failure
  // fallback (see FAILURE POLICY). It is in a data- attribute, never in src.
  function _refAttrs(r, o) {
    let out = ` data-sf-path="${_esc(r.path)}"`;
    if (r.bucket) out += ` data-sf-bucket="${_esc(r.bucket)}"`;
    if (r.url && isStorageUrl(r.url)) out += ` data-sf-url="${_esc(r.url)}"`;
    return out + _doorAttrs(o);
  }

  // <img ${imgAttrs(x)} alt="…">
  function imgAttrs(input, opts) {
    const r = fileRef(input, opts);
    if (!r) return `src="${PLACEHOLDER}"`;
    if (!r.path) return `src="${_esc(_safeExternal(r.url) || PLACEHOLDER)}"`;
    return `src="${PLACEHOLDER}" data-sf-img="1"${_refAttrs(r, opts)}`;
  }

  // <a ${linkAttrs(x)}>…</a>   (anchor keeps role/keyboard semantics)
  function linkAttrs(input, opts) {
    const r = fileRef(input, opts);
    if (!r) return 'href="#" data-sf-open="1" data-sf-missing="1"';
    if (!r.path) {
      const ext = _safeExternal(r.url);
      return ext ? `href="${_esc(ext)}" target="_blank" rel="noopener noreferrer"` : 'href="#" data-sf-open="1" data-sf-missing="1"';
    }
    return `href="#" data-sf-open="1"${_refAttrs(r, opts)}`;
  }

  function _refFromEl(el) {
    const path = el.getAttribute('data-sf-path');
    if (!path) return null;
    let doorArgs = null;
    const da = el.getAttribute('data-sf-door-args');
    if (da) { try { doorArgs = JSON.parse(da); } catch (_) {} }
    return {
      path,
      bucket: el.getAttribute('data-sf-bucket') || null,
      url: el.getAttribute('data-sf-url') || null,
      door: el.getAttribute('data-sf-door') || null,
      doorArgs,
    };
  }

  function _storageFor(bucket) {
    if (!bucket || !_sdk.getStorage) return _sdk.storage;
    try {
      const def = _sdk.storage && _sdk.storage.app && _sdk.storage.app.options && _sdk.storage.app.options.storageBucket;
      if (def && def === bucket) return _sdk.storage;
      return _sdk.getStorage(_sdk.storage.app, 'gs://' + bucket);
    } catch (_) { return _sdk.storage; }
  }

  // Simple concurrency gate so a 60-photo ticket does not fire 60 fetches.
  let _active = 0;
  const _queue = [];
  function _gate(fn) {
    return new Promise((resolve, reject) => {
      const run = () => {
        _active++;
        Promise.resolve().then(fn).then(resolve, reject).finally(() => {
          _active--;
          const next = _queue.shift();
          if (next) next();
        });
      };
      if (_active < MAX_CONCURRENT) run(); else _queue.push(run);
    });
  }

  function _code(err) { return (err && (err.code || err.name)) || 'unknown'; }

  function _typed(blob) {
    if (blob && SAFE_TYPE.test(blob.type || '')) return blob;
    return new Blob([blob], { type: 'application/octet-stream' });
  }

  function _remember(key, entry) {
    _cache.set(key, entry);
    while (_cache.size > CACHE_MAX) {
      const oldest = _cache.keys().next().value;
      const old = _cache.get(oldest);
      _cache.delete(oldest);
      if (old && old.objectUrl) { try { URL.revokeObjectURL(old.objectUrl); } catch (_) {} }
    }
  }

  // Resolve a ref to something an <img>/<a> can load.
  // → { kind: 'blob', objectUrl } | { kind: 'url', url } (transitional fallback)
  // Throws an Error with .sfReason = 'denied' | 'missing' | 'failed'.
  async function resolve(input, opts) {
    const o = opts || {};
    const r = (input && input.path !== undefined && input.door !== undefined) ? input : (function () {
      const fr = fileRef(input, o);
      return fr ? Object.assign({}, fr, { door: o.door || null, doorArgs: o.doorArgs || null }) : null;
    })();
    if (!r) { const e = new Error('No file'); e.sfReason = 'missing'; throw e; }
    if (!r.path) {
      const ext = _safeExternal(r.url);
      if (ext) return { kind: 'url', url: ext };
      const e = new Error('No file'); e.sfReason = 'missing'; throw e;
    }
    if (!_sdk) { const e = new Error('StorageFile not initialised'); e.sfReason = 'failed'; throw e; }

    const gen = _gen;
    // A door-capable read is cached apart from a rules-only one, so a blob a door
    // granted can never answer a rules-only read (open() in the iOS shell hands
    // the link over only after a rules-only read — hotfix 1.15.2100.1).
    const key = _identity() + '|' + (r.bucket || '') + '|' + r.path + (r.door ? '|door' : '');
    const hit = _cache.get(key);
    if (hit) { _cache.delete(key); _cache.set(key, hit); return hit.promise; }

    const promise = _gate(async () => {
      let blob;
      try {
        blob = await _sdk.getBlob(_sdk.ref(_storageFor(r.bucket), r.path));
        stats.sdk++;
      } catch (err) {
        const code = _code(err);
        if (DENIED.indexOf(code) !== -1) {
          const door = r.door && _doors[r.door];
          if (door) {
            try {
              blob = await door({ path: r.path, bucket: r.bucket, args: r.doorArgs || {} });
              stats.door++;
            } catch (dErr) {
              stats.denied++;
              const e = new Error((dErr && dErr.message) || 'No access'); e.sfReason = 'denied'; e.cause = dErr;
              // The door's own refusal text is written for the person ("not on one
              // of your jobs — ask dispatch"); show it rather than a generic line.
              if (dErr && /permission-denied|not-found/.test(String(dErr.code || '')) && dErr.message) e.userMessage = String(dErr.message).replace(/^[a-z-]+:\s*/, '');
              throw e;
            }
          } else {
            stats.denied++;
            const e = new Error('No access to this file'); e.sfReason = 'denied'; e.cause = err; throw e;
          }
        } else if (MISSING.indexOf(code) !== -1) {
          stats.missing++;
          const e = new Error('File not found'); e.sfReason = 'missing'; e.cause = err; throw e;
        } else if (r.url && isStorageUrl(r.url) && !r.door) {
          // TASK-10086-N — a door ref never falls back to its stored link (the
          // door's class check is the decision; a network error is not one).
          // COD delta: never hand a previous user's stored link to the page.
          if (gen !== _gen) { const e = new Error('Signed-in user changed'); e.sfReason = 'failed'; throw e; }
          // TRANSITIONAL (until tokens are revoked): network/CORS/offline only.
          stats.urlFallback++;
          try { console.warn('[StorageFile] SDK read failed (' + code + '), using stored link for', r.path.split('/').slice(0, 3).join('/') + '/…'); } catch (_) {}
          return { kind: 'url', url: r.url };
        } else {
          const e = new Error('Could not load the file'); e.sfReason = 'failed'; e.cause = err; throw e;
        }
      }
      if (gen !== _gen) { const e = new Error('Signed-in user changed'); e.sfReason = 'failed'; throw e; }
      const typed = _typed(blob);
      const objectUrl = URL.createObjectURL(typed);
      const entry = _cache.get(key);
      if (entry) entry.objectUrl = objectUrl;
      return { kind: 'blob', objectUrl, type: typed.type || '' };
    });
    _remember(key, { promise, objectUrl: null });
    // A failure — or a transitional URL fallback — must not stick in the cache:
    // the next view after sign-in / reconnect should go through the SDK again.
    const _drop = () => { if (_cache.get(key) && _cache.get(key).promise === promise) _cache.delete(key); };
    promise.then((res) => { if (res && res.kind !== 'blob') _drop(); }, _drop);
    return promise;
  }

  async function blobUrl(input, opts) {
    const res = await resolve(input, opts);
    return res.kind === 'blob' ? res.objectUrl : res.url;
  }

  function _toast(msg, type) {
    try { if (root && typeof root.showToast === 'function') root.showToast(msg, type || 'error'); } catch (_) {}
  }

  function _reasonText(err) {
    const r = err && err.sfReason;
    if (err && err.userMessage) return err.userMessage;
    if (r === 'denied') return "You don't have access to this file.";
    if (r === 'missing') return 'That file is missing.';
    return 'Could not open the file. Check your connection and try again.';
  }

  // P0 2026-10-19 (hotfix 1.15.2100.1) — shells that cannot open a blob: URL.
  // The iOS app is a Capacitor WKWebView on https://dispatchdeck.app. Its
  // createWebViewWith hands every window.open / target=_blank URL to
  // UIApplication.open() and returns nil (ionic-team/capacitor
  // WebViewDelegationHandler.swift). So open() below gave iOS 'about:blank'
  // and then 'blob:https://dispatchdeck.app/…', and Safari can open neither.
  // The tap did nothing, with no toast. Every permit, photo and doc tap in the
  // app broke this way from 1.15.2090.0 (TASK-9855 S1). Before that, the anchor
  // carried the https token URL and iOS opened it in Safari. The iOS home-screen
  // PWA and iOS in-app browsers are the same WKWebView without "Safari/" in the
  // UA, and they cannot hand a blob to a new window either.
  function _noBlobWindows() {
    try {
      const cap = root && root.Capacitor;
      // iOS shell only: Android Capacitor/TWA is not this bug and keeps the blob flow.
      if (cap && typeof cap.isNativePlatform === 'function' && cap.isNativePlatform()) {
        return typeof cap.getPlatform !== 'function' || cap.getPlatform() === 'ios';
      }
    } catch (_) {}
    try {
      const nav = root && root.navigator;
      if (!nav) return false;
      if (nav.standalone === true) return true;
      const ua = String(nav.userAgent || '');
      return /iPhone|iPad|iPod/.test(ua) && !/Safari\//.test(ua);
    } catch (_) { return false; }
  }

  // TASK-9978 — the native Capacitor app only (iOS or Android). Not the iOS
  // home-screen web app, not an in-app browser: those are _noBlobWindows() too,
  // but a main-frame navigation there replaces the app page itself.
  function _isNativeShell() {
    try {
      const cap = root && root.Capacitor;
      return !!(cap && typeof cap.isNativePlatform === 'function' && cap.isNativePlatform());
    } catch (_) { return false; }
  }

  // TASK-9978 — a Google/Firebase Storage host. A URL on one of these that
  // pathFromUrl could not turn into a path is a damaged file link, never an
  // "external" site.
  function _isStorageHost(url) {
    let h;
    try { h = new URL(String(url)).hostname.toLowerCase(); } catch (_) { return false; }
    return h === 'firebasestorage.googleapis.com' || h === 'storage.googleapis.com'
      || h === 'storage.cloud.google.com' || /\.storage\.googleapis\.com$/.test(h)
      || /(^|\.)firebasestorage\.app$/.test(h);
  }

  // Hotfix 2026-10-19 (county permits) — the iOS shell's rules check + link.
  // storage.rules `read` covers metadata exactly as it covers the bytes, so a
  // metadata read is the same decision without downloading the file.
  //   same stored https link → getMetadata(path), then that stored link
  //   path only              → getDownloadURL(path), accepted only if it is an
  //                            https link to that exact object
  // Throws .sfReason 'denied' | 'missing' | 'failed'. A network failure on a
  // non-door ref with the same stored link keeps the transitional fallback
  // (as resolve() does); a door ref never gets a link without a rules decision.
  async function _shellLink(r, same, hadDoor) {
    const sref = _sdk.ref(_storageFor(r.bucket), r.path);
    try {
      if (same) {
        await _sdk.getMetadata(sref);
        stats.shellMeta = (stats.shellMeta || 0) + 1;
        return r.url;
      }
      const link = await _sdk.getDownloadURL(sref);
      const p = typeof link === 'string' && /^https:\/\//i.test(link) ? pathFromUrl(link) : null;
      if (!p || p.path !== r.path || (r.bucket && p.bucket !== r.bucket)) {
        const e = new Error('Link is not this file'); e.sfReason = 'failed'; throw e;
      }
      stats.shellMeta = (stats.shellMeta || 0) + 1;
      return link;
    } catch (err) {
      if (err && err.sfReason) throw err;
      const code = _code(err);
      if (DENIED.indexOf(code) !== -1) {
        stats.denied++;
        const e = new Error('No access to this file'); e.sfReason = 'denied'; e.cause = err; throw e;
      }
      if (code === 'storage/no-download-url') {
        // Merge with fix/ios-door-docs-1019: the object exists but has no download
        // token, so there is no link to hand Safari. open() falls back to the
        // rules-checked byte read and the in-app viewer.
        const e = new Error('No link for this file'); e.sfReason = 'missing'; e.sfNoLink = true; e.cause = err; throw e;
      }
      if (MISSING.indexOf(code) !== -1) {
        stats.missing++;
        const e = new Error('File not found'); e.sfReason = 'missing'; e.cause = err; throw e;
      }
      if (same && !hadDoor) {
        stats.urlFallback++;
        try { console.warn('[StorageFile] metadata read failed (' + code + '), using stored link for', r.path.split('/').slice(0, 3).join('/') + '/…'); } catch (_) {}
        return r.url;
      }
      const e = new Error('Could not load the file'); e.sfReason = 'failed'; e.cause = err; throw e;
    }
  }


  // Fix 2026-10-19 — in-app viewer for the iOS shell, used only when the rules
  // ALLOWED the read but there is no https link for the same object to hand to
  // Safari (a path-only ref). The bytes are already here; nothing new is minted.
  // Images show in an <img>; a PDF in an <iframe> (the WKWebView renders it);
  // anything else is not shown (_typed() re-typed it octet-stream).
  function _showViewer(res) {
    const d = root && root.document;
    if (!d || !d.body) return false;
    const old = d.getElementById('sf-viewer');
    if (old) { if (typeof old.__sfClose === 'function') old.__sfClose(); else old.remove(); }
    const wrap = d.createElement('div');
    wrap.id = 'sf-viewer';
    wrap.setAttribute('role', 'dialog');
    wrap.setAttribute('aria-modal', 'true');
    wrap.setAttribute('aria-label', 'File viewer');
    wrap.style.cssText = 'position:fixed;inset:0;z-index:2147483000;background:#0F172A;display:flex;flex-direction:column;height:100vh;height:100dvh;';
    const bar = d.createElement('div');
    bar.style.cssText = 'flex:0 0 auto;display:flex;justify-content:flex-end;padding:calc(env(safe-area-inset-top, 0px) + 6px) 8px 6px;';
    const btn = d.createElement('button');
    btn.type = 'button';
    btn.textContent = 'Close';
    btn.style.cssText = 'min-height:44px;min-width:88px;padding:0 16px;font-size:17px;font-weight:600;color:#fff;background:#3B82F6;border:0;border-radius:8px;';
    bar.appendChild(btn);
    const body = d.createElement('div');
    body.style.cssText = 'flex:1 1 auto;overflow:auto;-webkit-overflow-scrolling:touch;background:#fff;';
    const type = String((res && res.type) || '');
    if (/^image\//i.test(type)) {
      const img = d.createElement('img');
      img.src = res.objectUrl;
      img.alt = '';
      img.style.cssText = 'display:block;max-width:100%;height:auto;margin:0 auto;';
      body.appendChild(img);
    } else if (/^application\/pdf$/i.test(type)) {
      const fr = d.createElement('iframe');
      fr.src = res.objectUrl;
      fr.title = 'Document';
      fr.style.cssText = 'display:block;width:100%;height:100%;border:0;';
      body.appendChild(fr);
    } else {
      const p = d.createElement('p');
      p.textContent = "This file type can't be shown in the app. Ask dispatch for a copy.";
      p.style.cssText = 'padding:24px 16px;font-size:17px;color:#0F172A;';
      body.appendChild(p);
    }
    const onKey = (ev) => { if (ev.key === 'Escape') close(); };
    function close() { try { d.removeEventListener('keydown', onKey, true); } catch (_) {} wrap.remove(); }
    wrap.__sfClose = close; // reset() and a replacing viewer go through the same cleanup
    btn.addEventListener('click', close);
    d.addEventListener('keydown', onKey, true);
    wrap.appendChild(bar);
    wrap.appendChild(body);
    d.body.appendChild(wrap);
    try { btn.focus(); } catch (_) {}
    stats.shellViewer = (stats.shellViewer || 0) + 1;
    return true;
  }

  // Rules denied a door ref in the iOS shell: ask the door for its short-lived
  // signed link (the server runs the door's own checks) and navigate the main
  // frame to it, so the shell hands it to Safari. A refusal shows the door's own
  // words ("not on one of your jobs — ask dispatch"); a link that is not a
  // short-lived signed URL for this exact object is never followed.
  async function _openDoorLink(link, r) {
    const gen = _gen;
    try {
      const url = await link({ path: r.path, bucket: r.bucket, args: r.doorArgs || {} });
      if (gen !== _gen) { const e = new Error('Signed-in user changed'); e.sfReason = 'failed'; throw e; }
      if (!_isSignedLinkFor(url, r)) { const e = new Error('Bad link'); e.sfReason = 'failed'; throw e; }
      stats.doorLink = (stats.doorLink || 0) + 1;
      root.location.href = url;
      return true;
    } catch (dErr) {
      stats.denied++;
      let msg = null;
      if (dErr && /permission-denied|not-found|unavailable/.test(String(dErr.code || '')) && dErr.message) msg = String(dErr.message).replace(/^[a-z-]+:\s*/, '');
      // A bare code word ("unavailable" from an infra 503) is not a sentence.
      if (msg && !/\s/.test(msg.trim())) msg = null;
      _toast(msg || _reasonText({ sfReason: 'failed' }));
      try { console.warn('[StorageFile] door link failed:', dErr && (dErr.code || dErr.sfReason)); } catch (_) {}
      return false;
    }
  }

  // Open a file in a new tab. Call from a click handler: the tab is opened
  // synchronously (popup blockers), then pointed at the object URL.
  async function open(input, opts) {
    // P0 2026-10-19: in a shell that cannot open a blob, the read still goes
    // through resolve() first, so storage.rules (or the named door) decide
    // exactly as on every other device, and a denial still never opens the
    // link. Only after that read is ALLOWED is the file's https link handed to
    // the shell, by a MAIN-FRAME navigation. Capacitor's decidePolicyFor sends a
    // main-frame navigation to a non-app host to UIApplication.open() and
    // cancels it (no user gesture is needed, unlike window.open after an await),
    // so Safari opens the PDF and the app page stays where it was.
    // ⚠️ TASK-9855 slice 5 (token revocation) must give this branch a
    // short-lived link before tokens go, or the iOS app breaks again.
    // Fix 2026-10-19: a rules DENIAL on a door ref now asks the door for its
    // short-lived signed link (same server checks as the bytes) and navigates the
    // main frame to it; a rules-ALLOWED read with no same-object https link (a
    // path-only ref) shows the bytes in an in-app viewer.
    if (_noBlobWindows()) {
      const o = opts || {};
      const r = (input && input.path !== undefined && input.door !== undefined) ? input : (function () {
        const fr = fileRef(input, o);
        return fr ? Object.assign({}, fr, { door: o.door || null, doorArgs: o.doorArgs || null }) : null;
      })();
      // COD folds: (1) the link handed over must be the SAME object the read
      // authorised, never a URL that merely sits beside another path; (2) only
      // storage.rules may authorise the handover. A door grants a file the rules
      // deny, and handing out its bearer token would outlive that grant, so the
      // read here runs with the door REMOVED (and its own cache key, see
      // resolve). Rules allow (office staff on a vehicle doc) → open it; rules
      // deny on a door ref (a driver's vehicle doc) → a toast, nothing opens.
      const u = r && r.url ? pathFromUrl(r.url) : null;
      const same = !!(u && r.path && u.path === r.path && (!r.bucket || u.bucket === r.bucket)
        && /^https:\/\//i.test(r.url));
      // Hotfix 2026-10-19 (county permits): an external https link (no Storage
      // path, e.g. a county/LoadPass portal URL) needs no SDK read: it goes
      // straight to Safari by a main-frame navigation. window.open('') below
      // would hand iOS 'about:blank'.
      // TASK-9978 — two limits on that shortcut:
      //  (a) a Storage-host URL whose path did not parse never takes it (it
      //      would skip the rules check): "damaged link" toast, nothing opens;
      //  (b) only the native Capacitor shell navigates its MAIN FRAME to the
      //      link, because only there is a non-app host guaranteed to go to
      //      Safari. The iOS home-screen web app and in-app browsers would put
      //      the stored site INSIDE the app window with no address bar (a look-
      //      alike login page), so they get a new tab instead (window.open,
      //      synchronous — no await before it — so it counts as the user's tap).
      if (r && !r.path) {
        const ext = _safeExternal(r.url);
        if (ext && /^https:\/\//i.test(ext)) {
          if (_isStorageHost(ext)) {
            _toast("This file link is damaged and can't be opened. Ask dispatch to re-attach the file.");
            return false;
          }
          stats.shellUrl = (stats.shellUrl || 0) + 1;
          if (_isNativeShell()) { root.location.href = ext; return true; }
          // COD fold (Code Reviewer / Independent / Domain): an anchor click can be
          // dropped silently by a WKWebView, so use window.open — it says when
          // nothing opened — and tell the person instead of a dead tap.
          let w = null;
          try { w = root.open(ext, '_blank'); } catch (_) { w = null; }
          if (w) { try { w.opener = null; } catch (_) {} return true; }
          _toast("This link couldn't open here. Open DispatchDeck in Safari, or ask dispatch for the link.");
          return false;
        }
      }
      if (r && r.path) {
        const doorName = r.door || o.door || null;
        const hadDoor = !!doorName;
        // Hotfix 2026-10-19 (county permits): the rules check is a METADATA read
        // (same storage.rules `read` decision, a few hundred bytes), not the whole
        // file — a 3-4 MB county permit downloaded on field LTE took long enough
        // that the tap looked dead and Capacitor dropped the hand-off. A path-only
        // ref gets its https link from getDownloadURL (the same rules-checked
        // read, only for that exact object). Merge with fix/ios-door-docs-1019:
        // this metadata check is the authorization step; a rules denial on a door
        // ref goes to the door's short-lived signed link, and only a ref with no
        // usable link (no download token) falls through to the rules-checked
        // byte read + in-app viewer below.
        const cheap = !!(_sdk && (same ? typeof _sdk.getMetadata === 'function' : typeof _sdk.getDownloadURL === 'function'));
        if (cheap) {
          const gen0 = _gen;
          let noLink = false;
          try {
            // COD fold (AuthZ): a ref with no bucket reads metadata in the bucket
            // the stored link names, so the object checked IS the object handed over.
            const link = await _shellLink((same && !r.bucket) ? Object.assign({}, r, { bucket: u.bucket }) : r, same, hadDoor);
            if (gen0 !== _gen) { const e = new Error('Signed-in user changed'); e.sfReason = 'failed'; throw e; }
            stats.shellUrl = (stats.shellUrl || 0) + 1;
            root.location.href = link;
            return true;
          } catch (err) {
            if (err && err.sfNoLink && !same) {
              noLink = true;
            } else {
              const dl = hadDoor && err && err.sfReason === 'denied' ? _doorLinks[doorName] : null;
              if (dl) return _openDoorLink(dl, r);
              _toast(hadDoor && err && err.sfReason === 'denied'
                ? "This vehicle file can't open in the app yet. Open DispatchDeck in Safari, or ask dispatch."
                : _reasonText(err));
              try { console.warn('[StorageFile] open failed:', err && err.sfReason, err && err.cause && _code(err.cause)); } catch (_) {}
              return false;
            }
          }
          if (!noLink) return false;
        }
        const rulesOnly = Object.assign({}, r, { door: null, doorArgs: null });
        const gen = _gen;
        let res;
        try {
          res = await resolve(rulesOnly, Object.assign({}, o, { door: null, doorArgs: null }));
          // Signed out / switched user while the read was in flight: show nothing.
          if (gen !== _gen) { const e = new Error('Signed-in user changed'); e.sfReason = 'failed'; throw e; }
        } catch (err) {
          const link = hadDoor && err && err.sfReason === 'denied' ? _doorLinks[doorName] : null;
          if (link) return _openDoorLink(link, r);
          _toast(hadDoor && err && err.sfReason === 'denied'
            ? "This vehicle file can't open in the app yet. Open DispatchDeck in Safari, or ask dispatch."
            : _reasonText(err));
          try { console.warn('[StorageFile] open failed:', err && err.sfReason, err && err.cause && _code(err.cause)); } catch (_) {}
          return false;
        }
        if (res.kind === 'blob') {
          // Rules allowed. The same object's https link goes to Safari (as before).
          if (same) {
            stats.shellUrl = (stats.shellUrl || 0) + 1;
            root.location.href = r.url;
            return true;
          }
          return _showViewer(res);
        }
        // A network-failure fallback is not a rules decision (COD fold): only a
        // non-door ref whose link is the same object may still go to Safari.
        if (same && !hadDoor) {
          stats.shellUrl = (stats.shellUrl || 0) + 1;
          root.location.href = res.url;
          return true;
        }
        _toast(_reasonText({ sfReason: 'failed' }));
        return false;
      }
      // No path (an external link, or nothing): the flow below, unchanged.
    }
    let w = null;
    try { w = root.open('', '_blank'); } catch (_) { w = null; }
    try { if (w) w.opener = null; } catch (_) {}
    try {
      const href = await blobUrl(input, opts);
      if (w && !w.closed) { w.location.href = href; return true; }
      const a = root.document.createElement('a');
      a.href = href; a.target = '_blank'; a.rel = 'noopener noreferrer';
      root.document.body.appendChild(a); a.click(); a.remove();
      return true;
    } catch (err) {
      try { if (w && !w.closed) w.close(); } catch (_) {}
      _toast(_reasonText(err));
      try { console.warn('[StorageFile] open failed:', err && err.sfReason, err && err.cause && _code(err.cause)); } catch (_) {}
      return false;
    }
  }

  function _hydrateImg(img) {
    if (!img || img.getAttribute('data-sf-state')) return;
    const r = _refFromEl(img);
    if (!r) return;
    img.setAttribute('data-sf-state', 'loading');
    resolve(r).then((res) => {
      if (img.getAttribute('data-sf-path') !== r.path) return; // re-used element
      img.src = res.kind === 'blob' ? res.objectUrl : res.url;
      img.setAttribute('data-sf-state', res.kind === 'blob' ? 'ok' : 'url');
    }).catch((err) => {
      img.setAttribute('data-sf-state', (err && err.sfReason) || 'failed');
      if (!img.getAttribute('title')) img.setAttribute('title', _reasonText(err));
    });
  }

  let _io = null;
  function _observeImg(img) {
    if (img.getAttribute('data-sf-state') || img.__sfSeen) return;
    img.__sfSeen = true;
    if (_io) _io.observe(img); else _hydrateImg(img);
  }

  function hydrate(scope) {
    const rootEl = scope || (root && root.document);
    if (!rootEl || !rootEl.querySelectorAll) return;
    if (rootEl.matches && rootEl.matches('img[data-sf-img]')) _observeImg(rootEl);
    const imgs = rootEl.querySelectorAll('img[data-sf-img]');
    for (let i = 0; i < imgs.length; i++) _observeImg(imgs[i]);
  }

  let _domInstalled = false;
  function _installDom() {
    if (_domInstalled || !root || !root.document) return;
    _domInstalled = true;
    const d = root.document;
    if (typeof root.IntersectionObserver === 'function') {
      _io = new root.IntersectionObserver((entries) => {
        entries.forEach((en) => {
          if (en.isIntersecting || en.intersectionRatio > 0) { _io.unobserve(en.target); _hydrateImg(en.target); }
        });
      }, { rootMargin: '300px' });
    }
    if (typeof root.MutationObserver === 'function') {
      const mo = new root.MutationObserver((muts) => {
        for (let i = 0; i < muts.length; i++) {
          const m = muts[i];
          if (m.type === 'attributes') {
            const t = m.target;
            if (t && t.tagName === 'IMG' && t.hasAttribute('data-sf-img')) {
              t.removeAttribute('data-sf-state'); t.__sfSeen = false; _observeImg(t);
            }
            continue;
          }
          const added = m.addedNodes;
          for (let j = 0; j < added.length; j++) { if (added[j].nodeType === 1) hydrate(added[j]); }
        }
      });
      const start = () => mo.observe(d.documentElement || d.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-sf-path'] });
      if (d.documentElement) start(); else d.addEventListener('DOMContentLoaded', start);
    }
    d.addEventListener('click', (ev) => {
      const t = ev.target && ev.target.closest ? ev.target.closest('[data-sf-open]') : null;
      if (!t) return;
      ev.preventDefault();
      if (t.hasAttribute('data-sf-missing')) { _toast('That file is missing.'); return; }
      const r = _refFromEl(t);
      if (r) open(r);
    }, true);
    hydrate(d);
  }

  return {
    // pure
    pathFromUrl, isStorageUrl, fileRef, pathKeysFor, isSafePath,
    // browser
    init, reset, registerDoor, imgAttrs, linkAttrs, resolve, blobUrl, open, hydrate,
    PLACEHOLDER, stats,
  };
});
